import { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  MessageSquare,
  Database,
  Users,
  Settings,
  ChevronDown,
  ChevronRight,
  Menu,
  X,
  Shield,
  Briefcase,
  FolderKanban,
  Megaphone,
  Bell,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useFetch } from "@/hooks/use-fetch";

interface AdminUser {
  name: string;
  email: string;
  role: string;
}

interface MeResponse {
  data: AdminUser;
}

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  children?: { to: string; label: string }[];
}

const navItems: NavItem[] = [
  {
    to: "/",
    label: "Dashboard",
    icon: LayoutDashboard,
  },
  {
    to: "/forms",
    label: "İletişim Formları",
    icon: MessageSquare,
  },
  {
    to: "/clients",
    label: "Müşteriler",
    icon: Briefcase,
  },
  {
    to: "/projects",
    label: "Projeler",
    icon: FolderKanban,
  },
  {
    to: "/call-periods",
    label: "Çağrı Dönemleri",
    icon: Megaphone,
  },
  {
    to: "/reminders",
    label: "Hatırlatıcılar",
    icon: Bell,
  },
  {
    to: "/data",
    label: "Teşvik Verileri",
    icon: Database,
    children: [
      { to: "/data/nace", label: "NACE Kodları" },
      { to: "/data/ek3", label: "EK-3 Listesi" },
      { to: "/data/regions", label: "Bölgeler" },
    ],
  },
  {
    to: "/users",
    label: "Kullanıcılar",
    icon: Users,
  },
  {
    to: "/settings",
    label: "Ayarlar",
    icon: Settings,
  },
];

export function Sidebar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const location = useLocation();
  const { data: me } = useFetch<MeResponse>("/me");

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname.startsWith("/data")) {
      setExpandedItems((prev) =>
        prev.includes("/data") ? prev : [...prev, "/data"]
      );
    }
  }, [location.pathname]);

  function toggleExpanded(to: string) {
    setExpandedItems((prev) =>
      prev.includes(to) ? prev.filter((i) => i !== to) : [...prev, to]
    );
  }

  const sidebarContent = (
    <div className="flex h-full flex-col">
      {/* Logo */}
      <div className="flex h-16 items-center gap-3 border-b border-border px-5">
        <img src="/logo.jpeg" alt="YSR" className="h-9 w-9 rounded-lg object-cover" />
        <div>
          <span className="text-sm font-semibold text-foreground">YSR</span>
          <span className="ml-1 text-xs text-muted-foreground">Admin</span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        <ul className="space-y-1">
          {navItems.map((item) => (
            <li key={item.to}>
              {item.children ? (
                <div>
                  <button
                    onClick={() => toggleExpanded(item.to)}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                      location.pathname.startsWith(item.to)
                        ? "text-accent"
                        : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                    )}
                  >
                    <item.icon className="h-4 w-4 shrink-0" />
                    <span className="flex-1 text-left">{item.label}</span>
                    {expandedItems.includes(item.to) ? (
                      <ChevronDown className="h-3.5 w-3.5" />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5" />
                    )}
                  </button>
                  {expandedItems.includes(item.to) && (
                    <ul className="ml-7 mt-1 space-y-1 border-l border-border pl-3">
                      {item.children.map((child) => (
                        <li key={child.to}>
                          <NavLink
                            to={child.to}
                            className={({ isActive }) =>
                              cn(
                                "block rounded-md px-3 py-2 text-sm transition-colors",
                                isActive
                                  ? "bg-accent/10 text-accent font-medium"
                                  : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                              )
                            }
                          >
                            {child.label}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : (
                <NavLink
                  to={item.to}
                  end={item.to === "/"}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                      isActive
                        ? "bg-accent/10 text-accent"
                        : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                    )
                  }
                >
                  <item.icon className="h-4 w-4 shrink-0" />
                  <span>{item.label}</span>
                </NavLink>
              )}
            </li>
          ))}
        </ul>
      </nav>

      {/* User info */}
      {me?.data && (
        <div className="border-t border-border px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary text-xs font-medium text-foreground">
              {me.data.name
                .split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase()
                .slice(0, 2)}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-foreground">
                {me.data.name}
              </p>
              <p className="truncate text-xs text-muted-foreground">
                {me.data.role === "super_admin" ? "Süper Admin" : "Admin"}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Mobile toggle */}
      <button
        onClick={() => setMobileOpen(!mobileOpen)}
        className="fixed left-4 top-4 z-50 flex h-10 w-10 items-center justify-center rounded-lg bg-secondary text-foreground lg:hidden"
        aria-label={mobileOpen ? "Menüyü kapat" : "Menüyü aç"}
      >
        {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed left-0 top-0 z-40 h-full w-64 border-r border-border bg-card transition-transform duration-200 lg:translate-x-0",
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        {sidebarContent}
      </aside>
    </>
  );
}
